import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { workflowKeys } from './workflowKeys';

export interface BulkDeleteResult {
  total: number;
  succeeded: number;
  failed: number;
}

// Permanently deletes many workflows in a single action. Reuses the existing
// per-workflow DELETE endpoint (see usePermanentDeleteWorkflow) and fans the
// calls out with Promise.allSettled so one failure doesn't abort the rest —
// same approach as useBulkWorkflowStatus.
export function useBulkDeleteWorkflows() {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);

  return useMutation({
    mutationFn: async (ids: string[]): Promise<BulkDeleteResult> => {
      const base = `api/v1/t/${tenantId}/workflows`;
      const results = await Promise.allSettled(ids.map((id) => apiClient.delete(`${base}/${id}`)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { total: ids.length, succeeded: ids.length - failed, failed };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}
